import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { ArrowRight, Battery, TrendingUp, Shield, Zap, Clock, Users } from 'lucide-react';

const Info = () => {
  const { t, i18n } = useTranslation();
  const [bessContent, setBessContent] = useState({
    hero: {
      title: 'Was ist BESS?',
      subtitle: 'Battery Energy Storage Systems - Die Zukunft der Energiespeicherung'
    },
    intro: {
      text: 'BESS sind große Batteriespeichersysteme, die elektrische Energie speichern und bei Bedarf wieder ins Netz einspeisen. Sie spielen eine entscheidende Rolle bei der Integration erneuerbarer Energien und der Stabilisierung des Stromnetzes.'
    },
    benefits: {
      title: 'Warum in BESS investieren?',
      benefit1: 'Stabile Renditen',
      benefit2: 'Zukunftssicher',
      benefit3: 'Umweltfreundlich',
      benefit4: 'Langfristig'
    },
    investment: {
      title: 'So funktioniert\'s',
      text: 'Ihr Weg zur BESS-Investition in vier einfachen Schritten'
    }
  });

  const [infoContent, setInfoContent] = useState({
    steps: [
      { number: '1', title: 'Registrieren', text: 'Erstellen Sie ein Konto und verifizieren Sie Ihre E-Mail-Adresse' },
      { number: '2', title: 'Projekt auswählen', text: 'Durchstöbern Sie verfügbare Projekte und bekunden Sie Ihr Interesse' },
      { number: '3', title: 'Freischaltung', text: 'Nach Prüfung wird das Projekt für Sie freigeschaltet' },
      { number: '4', title: 'Investieren', text: 'Erwerben Sie Anteile und profitieren Sie von den Erträgen' }
    ],
    downloads: {
      flyer_url: '',
      details_url: ''
    }
  });

  useEffect(() => {
    fetchContent();
  }, [i18n.language]);

  const fetchContent = async () => {
    try {
      // Fetch BESS Info content
      const bessRes = await axios.get(`${API}/content/bess_info`, { params: { lang: i18n.language } });
      if (bessRes.data.content && Object.keys(bessRes.data.content).length > 0) {
        setBessContent(bessRes.data.content);
      }

      // Fetch Info Page content (steps and downloads)
      const infoRes = await axios.get(`${API}/content/info_page`, { params: { lang: i18n.language } });
      if (infoRes.data.content && Object.keys(infoRes.data.content).length > 0) {
        setInfoContent(infoRes.data.content);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      // Use default content if fetch fails
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="info-page">
      {/* Hero */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fadeIn">
            {bessContent.hero?.title || t('info.heroTitle')}
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            {bessContent.hero?.subtitle || t('info.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* What is BESS */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('info.whatIsTitle')}
              </h2>
              <p className="text-lg text-slate-700 mb-4 leading-relaxed whitespace-pre-line">
                {bessContent.intro?.text || t('info.introText')}
              </p>
              <div className="flex items-center space-x-2 text-green-600">
                <Battery size={24} />
                <span className="font-semibold">{t('info.keyTech')}</span>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxyZW5ld2FibGUlMjBlbmVyZ3l8ZW58MHx8fHwxNzYwMjU5MzA2fDA&ixlib=rb-4.1.0&q=85"
                alt="BESS Installation"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Invest */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {bessContent.benefits?.title || t('info.benefitsTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('info.benefitsSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {bessContent.benefits?.benefit1 && (
              <div className="card text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="text-green-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{bessContent.benefits.benefit1}</h3>
                <p className="text-slate-600">
                  {t('info.benefit1Text')}
                </p>
              </div>
            )}

            {bessContent.benefits?.benefit2 && (
              <div className="card text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="text-blue-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{bessContent.benefits.benefit2}</h3>
                <p className="text-slate-600">
                  {t('info.benefit2Text')}
                </p>
              </div>
            )}

            {bessContent.benefits?.benefit3 && (
              <div className="card text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="text-green-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{bessContent.benefits.benefit3}</h3>
                <p className="text-slate-600">
                  {t('info.benefit3Text')}
                </p>
              </div>
            )}

            {bessContent.benefits?.benefit4 && (
              <div className="card text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="text-slate-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{bessContent.benefits.benefit4}</h3>
                <p className="text-slate-600">
                  {t('info.benefit4Text')}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {bessContent.investment?.title || t('info.howItWorksTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {bessContent.investment?.text || t('info.howItWorksSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {infoContent.steps && infoContent.steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {step.number}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600">
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('info.ctaTitle')}</h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            {t('info.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/bess/register">
              <Button size="lg" className="bg-white text-green-600 hover:bg-slate-100 px-8">
                {t('info.ctaStart')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/bess/projects">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8">
                {t('info.ctaViewProjects')}
              </Button>
            </Link>
            {infoContent.downloads?.flyer_url && (
              <a href={infoContent.downloads.flyer_url} target="_blank" rel="noopener noreferrer" download>
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8">
                  {t('info.flyer')}
                </Button>
              </a>
            )}
            {infoContent.downloads?.details_url && (
              <a href={infoContent.downloads.details_url} target="_blank" rel="noopener noreferrer" download>
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8">
                  {t('info.allDetails')}
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Info;
